import { MenuItem, ListItemText, Box, Select, FormControl, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import IOSSwitch from 'src/shared/components/IosToggleSwitch';
import { useStyles } from './styles';
import Grid2 from '@mui/material/Unstable_Grid2';
import { GRID_COLUMN_CONFIG, GRID_CONTAINER_CONFIG } from '../consts';
import Feature from '../../Feature';
import { type CardDetailsGroup, type CardStatusToDisplay, NotRequestedCardStatus } from '../../types';
import { FlexflowCardStatus } from 'src/modules/Flexflow/types';
import { ContractFlexflowStatusToDisplay } from 'src/modules/Flexflow/Contracts/ContractStatusAfterInvitation';
import PostFreezeCardIcon from '../PostFreezeCardIcon';
import BlockCardModal from '../PostTerminateCardIcon/BlockCardModal';
import { useActionsSection } from './hooks';
import { MODAL_TYPES } from 'src/shared/components/Modals';
import { showModal } from 'src/old/actions';
import { useDispatch } from 'react-redux';

type ActionsSectionProps = {
  cardStatus?: CardStatusToDisplay;
  statusToDisplay?: ContractFlexflowStatusToDisplay;
  data?: CardDetailsGroup;
  payflowUserId: number;
  employeeDisabled: boolean;
};

export const ActionsSection = ({
  cardStatus,
  statusToDisplay,
  data,
  payflowUserId,
  employeeDisabled
}: ActionsSectionProps) => {
  const { t } = useTranslation(['flexflow']);
  const classes = useStyles();
  const dispatch = useDispatch();
  const {
    isLoading,
    values,
    cardFrozen,
    hasPhysicalCard,
    hasWalletFunds,
    toggleFreezeCard,
    handleTerminateCardConfirmClick
  } = useActionsSection({
    cardStatus,
    payflowUserId
  });

  const handleOpenModal = (cardMedium: 'physical' | 'virtual') => {
    const modalProps = {
      CustomComponent: () => (
        <BlockCardModal
          lastFour={values?.card.lastFour}
          action={async () => {
            await handleTerminateCardConfirmClick(cardMedium);
          }}
        />
      )
    };
    showModal(modalProps, MODAL_TYPES.CUSTOM)(dispatch);
  };

  if (
    cardStatus === NotRequestedCardStatus.NOT_REQUESTED ||
    cardStatus === FlexflowCardStatus.TERMINATED ||
    statusToDisplay === ContractFlexflowStatusToDisplay.SUSPENDED ||
    statusToDisplay === ContractFlexflowStatusToDisplay.REQUESTED ||
    !data?.createdAt
  ) {
    return null;
  }

  return (
    <>
      <Grid2 mb={4} sx={{ px: 0 }}>
        <Typography variant="h4" sx={{ fontWeight: 400 }}>
          {t('actions', { ns: 'common' })}
        </Typography>
      </Grid2>

      <Grid2 container {...GRID_CONTAINER_CONFIG}>
        <Grid2 {...GRID_COLUMN_CONFIG}>
          <FormControl fullWidth>
            <Select
              inputProps={{ 'aria-label': 'Without label' }}
              renderValue={() => `${t('freeze')} / ${t('block')}`}
              value=""
              displayEmpty
              MenuProps={{
                PaperProps: {
                  style: {
                    borderRadius: '4px'
                  }
                }
              }}
            >
              <MenuItem disableRipple>
                <Box className={classes.switchWrapper}>
                  <Feature
                    PostIcon={<PostFreezeCardIcon isSwanCard={Boolean(data?.isSwanCard)} />}
                    label={hasPhysicalCard ? t('freeze cards') : t('freeze card')}
                  ></Feature>
                  <IOSSwitch
                    onToggleClick={toggleFreezeCard}
                    disabled={isLoading || employeeDisabled || values.isEligible === false}
                    sourceChecked={cardFrozen}
                  />
                </Box>
              </MenuItem>
              {hasPhysicalCard && (
                <Tooltip
                  title={
                    hasWalletFunds
                      ? t('Cannot terminate card while employee has wallet funds. Please contact support.')
                      : ''
                  }
                  disableHoverListener={!hasWalletFunds}
                  classes={{ tooltip: classes.tooltip }}
                >
                  <span>
                    <MenuItem
                      onClick={() => {
                        handleOpenModal('physical');
                      }}
                      disabled={hasWalletFunds}
                    >
                      <Feature
                        PostIcon={<></>}
                        label={
                          <ListItemText
                            primary={t('block physical card')}
                            primaryTypographyProps={{
                              sx: {
                                color: hasWalletFunds ? '#999' : '#D10E0C'
                              }
                            }}
                          />
                        }
                      />
                    </MenuItem>
                  </span>
                </Tooltip>
              )}
              <Tooltip
                title={
                  hasWalletFunds
                    ? t('Cannot terminate card while employee has wallet funds. Please contact support.')
                    : ''
                }
                disableHoverListener={!hasWalletFunds}
                classes={{ tooltip: classes.tooltip }}
              >
                <span>
                  <MenuItem
                    onClick={() => {
                      handleOpenModal('virtual');
                    }}
                    disabled={hasWalletFunds}
                  >
                    <Feature
                      PostIcon={<></>}
                      label={
                        <ListItemText
                          primary={
                            hasPhysicalCard ? t('block physical card and virtual card') : t('block virtual card')
                          }
                          primaryTypographyProps={{
                            sx: {
                              color: '#D10E0C'
                            }
                          }}
                        />
                      }
                    />
                  </MenuItem>
                </span>
              </Tooltip>
            </Select>
          </FormControl>
        </Grid2>
      </Grid2>
    </>
  );
};
